import { useEffect, useState } from 'react';
import { adminApi } from '../../api/admin';
import { useSession } from '../../auth/session';
import { postAction } from '../../api/client';
import { useAsync, Banner } from './ui';

const NUMERIC = ['minIntervalMin', 'targetIntervalMin', 'dueGraceMin', 'overdueAfterMin', 'sessionHours', 'pmsCycleLengthDays'];
const HELP: Record<string, string> = {
  minIntervalMin: 'HARD minimum minutes between two readings for the same equipment. The only rule that blocks a submission.',
  targetIntervalMin: 'Recommended minutes between readings (the 2-hour schedule). Advisory only.',
  dueGraceMin: 'Minutes before the target when status becomes "Due".',
  overdueAfterMin: 'Minutes after the last reading when status becomes "Overdue".',
  sessionHours: 'How long an operator stays signed in on a device.',
  timezone: 'IANA timezone used for the Date/Time columns and the daily log, e.g. Asia/Kolkata.',
  tagSigningEnabled: 'true = tags must carry a valid ?k= signature. Turn on only after re-programming tags with signatures.',
  appMinVersion: 'Minimum app version allowed to submit (leave 0.0.0 unless forcing an update).',
  orgName: 'Institute name shown on the printed daily logbook (used only if the letterhead image fails to load).',
  orgSubtitle: 'Line under the institute name on the printed logbook.',
  letterheadUrl: 'Letterhead image for the printed daily logbook. Blank = the bundled /letterhead.svg. Set to /letterhead.png after adding your scan, or paste a full https URL / data: URI.',
  pmsCycleAnchorDate: 'The calendar date (yyyy-MM-dd) that counts as "day 1" of the rotating PMS schedule — pick any date and every PMS item\'s assigned day counts forward/back from it.',
  pmsCycleLengthDays: 'How many days the PMS rotation repeats over (e.g. 15). Each PMS equipment is assigned a day 1..N in Equipment; operators only see that item in their queue on its day.',
};
const LONG = ['orgName', 'orgSubtitle', 'letterheadUrl'];

export default function SettingsAdmin() {
  const { data, error, loading, reload } = useAsync(() => adminApi.getConfig(), [], 'config');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const session = useSession();
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState('');

  async function testAllPush() {
    if (!session) return;
    setPushBusy(true); setPushMsg('');
    try {
      const res = await postAction<{ ok: boolean; sent?: number; message?: string }>(
        'adminSendTestPushAll', { token: session.token },
      );
      setPushMsg(res.ok ? `Sent to ${res.sent} subscribed device${res.sent === 1 ? '' : 's'}.` : (res.message || 'Could not send.'));
    } catch (e) {
      setPushMsg(e instanceof Error ? e.message : 'Could not send.');
    } finally {
      setPushBusy(false);
    }
  }

  useEffect(() => {
    if (data) {
      const d: Record<string, string> = {};
      // start from effective config so new defaults (e.g. letterheadUrl) are editable
      Object.entries(data.effective ?? {}).forEach(([k, v]) => { d[k] = String(v ?? ''); });
      data.rows.forEach((r) => { d[String(r['Key'])] = String(r['Value'] ?? ''); });
      setDraft(d);
    }
  }, [data]);

  async function save() {
    setSaving(true); setMsg('');
    try {
      const updates: Record<string, unknown> = {};
      Object.entries(draft).forEach(([k, v]) => {
        updates[k] = NUMERIC.includes(k) ? Number(v) : v;
      });
      await adminApi.saveConfig(updates);
      setMsg('Saved.'); reload();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <h1>Settings</h1>
      <Banner error={error} ok={msg === 'Saved.' ? msg : undefined} />
      {msg && msg !== 'Saved.' && <p className="error">{msg}</p>}
      {loading && <div className="spinner" />}
      {data && (
        <>
          {Object.keys(draft).sort().map((k) => (
            <div key={k} style={{ marginBottom: 12 }}>
              <label>{k}</label>
              {LONG.includes(k)
                ? <textarea rows={k === 'letterheadUrl' ? 2 : 1} value={draft[k] ?? ''} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
                : <input inputMode={NUMERIC.includes(k) ? 'numeric' : 'text'} value={draft[k] ?? ''} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />}
              {HELP[k] && <p className="muted tiny" style={{ margin: '4px 0 0' }}>{HELP[k]}</p>}
            </div>
          ))}
          <button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>

          <h2>Push notifications</h2>
          <div className="card">
            <p className="muted tiny" style={{ marginTop: 0 }}>
              Sends a test push to every device currently subscribed to overdue-reading alerts
              (every user who's turned it on in their Profile / My account) — not just yours.
            </p>
            <button className="secondary" onClick={() => void testAllPush()} disabled={pushBusy}>
              {pushBusy ? 'Sending…' : 'Send test to all subscribed devices'}
            </button>
            {pushMsg && <p className="muted tiny" style={{ marginTop: 8 }}>{pushMsg}</p>}
          </div>
        </>
      )}
    </div>
  );
}
